import { useState, useCallback, useEffect } from "react";
import { setTokens, clearTokens, silentRefreshDetailed, hasImpersonationToken, endImpersonation, isPersonalDevice } from "../services/api";
import { hasFeature } from "../utils/featureFlags";

export interface AuthUser {
  user_id: string;
  user_name: string;
  user_role: string;
  company_id: string;
  short_id: string;
  avatar_path?: string | null;
}

/** ログイン中ユーザーの一部（アイコンなど）を書き換えて全画面に反映する */
export const USER_UPDATED_EVENT = 'catms:user-updated';
export function updateStoredUser(patch: Partial<AuthUser>): void {
  try {
    const cur = JSON.parse(localStorage.getItem('catms_user') ?? 'null') as AuthUser | null;
    if (!cur) return;
    const next = { ...cur, ...patch };
    localStorage.setItem('catms_user', JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(USER_UPDATED_EVENT, { detail: next }));
  } catch { /* 保存できなくても次回ログインで反映される */ }
}

export function useAuth() {
  const [user, setUser]       = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onUpdated = (e: Event) => setUser((e as CustomEvent<AuthUser>).detail);
    window.addEventListener(USER_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(USER_UPDATED_EVENT, onUpdated);
  }, []);

  useEffect(() => {
    const init = async () => {
      // 運営管理者モード: app-superadmin から #sa=<jwt> で開かれた場合、
      // トークンを取り込んでログイン状態にする（管理サイトと同じ方式）。
      // このトークンの user_id は会社のユーザー表に存在しない合成IDなので、
      // 打刻はできない（画面側で止める）。確認・閲覧用の入口。
      const m = window.location.hash.match(/[#&]sa=([^&]+)/);
      if (m && m[1]) {
        try {
          const token = decodeURIComponent(m[1]);
          const p = JSON.parse(atob(token.split('.')[1] ?? '')) as Record<string, unknown>;
          const u: AuthUser = {
            user_id: String(p['user_id'] ?? ''),
            user_name: String(p['user_name'] ?? '運営管理者'),
            user_role: String(p['user_role'] ?? 'system_admin'),
            company_id: String(p['company_id'] ?? ''),
            short_id: String(p['short_id'] ?? 'SA'),
            avatar_path: null,
          };
          setTokens(token);
          localStorage.setItem("catms_user", JSON.stringify(u));
          localStorage.setItem("catms_is_super_admin", p['is_super_admin'] === true ? '1' : '0');
          // トークンを履歴に残さない
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          setUser(u);
          setLoading(false);
          return;
        } catch { /* 不正なトークンは通常フローへ */ }
      }

      const stored = localStorage.getItem("catms_user");
      if (stored) {
        // 代理ログイン中はリフレッシュCookieが無い。sessionStorage の代理トークン（api側で復元済み）
        // をそのまま使い、silentRefresh を行わない。
        if (hasImpersonationToken()) {
          setUser(JSON.parse(stored) as AuthUser);
          setLoading(false);
          return;
        }
        // アクセストークンはメモリのみ保持のため、ページリロード時は cookie でサイレントリフレッシュ。
        // 圏外（サーバーに届かない）ときはログイン状態を保ったままにする。
        // 沖では通信できないのが普通で、ここで消すとアプリを開き直すたびにログイン画面へ戻り、
        // 保存してある勤怠履歴・乗下船表すら見られなくなるため。
        // サーバーから「認証できない」と返ってきたときだけログイン状態を消す。
        const r = await silentRefreshDetailed();
        if (r === 'unauthorized') {
          localStorage.removeItem("catms_user");
        } else if (r === 'offline' && hasFeature('crew_session_guard') && !isPersonalDevice()) {
          // 共有端末（crew_session_guard の会社）: 圏外でアプリを開き直したとき、前に使っていた人の
          // ログインを引き継がない（次の人が他人として打刻する事故を防ぐ）。端末に保存した情報での
          // 圏外ログインは今までどおりできる。個人スマホ（個人用アプリ）は従来どおり保ったままにする。
          // 勤務中の表示は catms_session_user の本人照合で復元されるため、ここでは消さない。
          clearTokens(); // catms_user も消える
        } else {
          setUser(JSON.parse(stored) as AuthUser);   // ok / offline
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const login = useCallback((userId: string, token: string, userData: Record<string, unknown>) => {
    const authUser: AuthUser = {
      user_id:     String(userData["user_id"]    ?? userId),
      user_name:   String(userData["user_name"]  ?? ""),
      user_role:   String(userData["user_role"]  ?? userData["role"] ?? "crew"),
      company_id:  String(userData["company_id"] ?? ""),
      short_id:    String(userData["short_id"]   ?? ""),
      avatar_path: (userData["avatar_path"] as string | null | undefined) ?? null,
    };
    setTokens(token);
    localStorage.setItem("catms_user", JSON.stringify(authUser));
    setUser(authUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("catms_is_super_admin");
    clearTokens();
    endImpersonation(); // 代理ログイン中ならトークン・フラグも破棄
    localStorage.removeItem("catms_token");
    localStorage.removeItem("catms_user");
    // crew_session_guard の会社では勤務中キーを消さずに残す。catms_session_user の本人照合付きなので
    // 他人には出ず、同じ人が圏外で再ログインしたとき「業務終了」を続けられる。他社は従来どおり。
    if (!hasFeature("crew_session_guard")) {
      localStorage.removeItem("catms_session_status");
      localStorage.removeItem("catms_work_session_id");
    }
    setUser(null);
  }, []);

  return { user, loading, login, logout, isAuthenticated: !!user };
}
